import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import slug from "slug";
import Icon from "../../components/Icon";
import Button from "../../components/Button";
import DropdownButton from "../../components/DropdownButton";
import EditableLabel from "../../components/EditableLabel";
import EmailWidthPicker from "../../components/EmailWidthPicker";
import {
  Toolbar,
  ToolbarGroup,
  ToolbarFlexibleSpacer,
} from "../../components/Toolbar";
import {
  downloadEmail,
  setEditorDisplayWidth,
  toggleEditorBreakdown,
  updateEmail,
  fetchEmailQA,
  openModal,
  updateEditorNameValue,
} from "../../actions";
import { history } from "../../history";

export class EditorToolbar extends Component {
  constructor(props) {
    super(props);
    this.setPreviewWidth = this.setPreviewWidth.bind(this);
    this.save = this.save.bind(this);
    this.download = this.download.bind(this);
    this.openSendModal = this.openSendModal.bind(this);
    this.onUpdateName = this.onUpdateName.bind(this);
    this.close = this.close.bind(this);
    this.openPreview = this.openPreview.bind(this);
    this.downloadPreview = this.downloadPreview.bind(this);
  }

  onUpdateName(newName) {
    this.props.updateEditorNameValue(newName);
  }

  close() {
    const { accountId, unsavedChanges, email } = this.props;

    const parentUrl = `/clients/${accountId}/campaigns/${email.campaignId}`;

    if (unsavedChanges) {
      this.props.openModal("confirm", {
        title: `Email has unsaved changes`,
        description: "Do you wish to save your changes first?",
        buttons: [
          {
            label: "Discard Changes",
            type: "link",
            actionFn: {
              name: "gotoUrl",
              arguments: [parentUrl],
            },
          },
          {
            label: "Save",
            type: "primary",
            actionFn: {
              name: "updateEmail",
              arguments: [accountId, email.id, email],
            },
          },
        ],
      });
      return;
    }

    history.push(parentUrl);
  }

  download(absoluteUrls) {
    const { accountId, unsavedChanges, email } = this.props;

    if (unsavedChanges) {
      this.props.openModal("confirm", {
        title: `Email has unsaved changes`,
        description: "Save changes before downloading",
        buttons: [
          {
            label: "Save",
            type: "primary",
            actionFn: {
              name: "updateEmail",
              arguments: [accountId, email.id, email],
            },
          },
        ],
      });
      return;
    }

    this.props.fetchEmailQA(accountId, email.id).then((action) => {
      if (action?.data?.length > 0) {
        this.props.openModal("confirm", {
          title: `Found ${action.data.length} QA Issues`,
          description: "Fix all QA issues before downloading",
          buttons: [
            {
              label: "View Issues",
              type: "link",
              actionFn: {
                name: "gotoUrl",
                arguments: [
                  `/clients/${accountId}/campaigns/${email.campaignId}/emails/${email.id}/qa`,
                ],
              },
            },
            {
              label: "Download Anyway",
              type: "primary",
              actionFn: {
                name: "downloadEmail",
                arguments: [accountId, email.id, absoluteUrls],
              },
            },
          ],
        });
      } else {
        this.props.downloadEmail(accountId, email.id, absoluteUrls);
      }
    });
  }

  openSendModal() {
    const { accountId, unsavedChanges, email } = this.props;
    const { id, translations } = email;

    if (unsavedChanges) {
      this.props.openModal("confirm", {
        title: `Email has unsaved changes`,
        description: "Save changes before sending",
        buttons: [
          {
            label: "Save",
            type: "primary",
            actionFn: {
              name: "updateEmail",
              arguments: [accountId, email.id, email],
            },
          },
        ],
      });
      return;
    }

    this.props.openModal("send-email", {
      accountId,
      id,
      translations,
    });
  }

  setPreviewWidth(width) {
    this.props.setEditorDisplayWidth(width);
  }

  openPreview() {
    const { accountId, email } = this.props;
    const url = `${process.env.REACT_APP_API_ENDPOINT}/api/clients/${accountId}/emails/${email.id}/html?var=${email.var}`;
    window.open(url, "_blank");
  }

  downloadPreview() {
    const { accountId, email } = this.props;
    const url = `${process.env.REACT_APP_API_ENDPOINT}/api/clients/${accountId}/emails/${email.id}/html?var=${email.var}`;
    const fileName = slug(email.name || email.subject || "email") + ".html";

    fetch(url)
      .then((response) => response.blob())
      .then((blob) => {
        // Create a temporary link to the blob content
        var link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = fileName;

        // Append the link to the DOM and trigger the download
        document.body.appendChild(link);
        link.click();

        // Clean up
        URL.revokeObjectURL(link.href);
        document.body.removeChild(link);
      });
  }

  save() {
    const { accountId } = this.props;
    if (this.props.email.id) {
      this.props.updateEmail(accountId, this.props.email.id, this.props.email);
    } else {
      console.log("CREATE NEW");
    }
  }

  render() {
    const { email, unsavedChanges, breakdown, displayWidth, accountId } =
      this.props;

    if (!email) {
      return <Toolbar />;
    }

    return (
      <Toolbar>
        <ToolbarGroup width="400px">
          <EditableLabel
            icon="envelope"
            value={email.name}
            onChange={this.onUpdateName}
            width="400px"
          />
        </ToolbarGroup>

        <ToolbarFlexibleSpacer />

        <ToolbarGroup width="400px" align="center">
          <EmailWidthPicker
            width={displayWidth}
            onChange={this.setPreviewWidth}
          />

          <Button
            type={breakdown ? " button" : "link"}
            onClick={this.props.toggleEditorBreakdown}
          >
            <Icon icon="align-justify" title="Breakdown" />
          </Button>
        </ToolbarGroup>

        <ToolbarFlexibleSpacer />

        <ToolbarGroup width="450px" align="right">
          <Button type="link" onClick={this.close} title="Close">
            <Icon icon="close" />
          </Button>
          <Link
            to={`/clients/${accountId}/campaigns/${email.campaignId}/emails/${email.id}/qa`}
          >
            <Button type="link" title="QA">
              <Icon icon="ambulance" />
            </Button>
          </Link>
          <Button type="link" onClick={this.openSendModal} title="Send">
            <Icon icon="envelope" />
          </Button>

          <Button
            type="link"
            onClick={() => this.download(false)}
            title="Download"
          >
            <Icon icon="download" />
          </Button>

          <DropdownButton
            type="link"
            pin="right"
            options={[
              {
                label: "Preview",
                onClick: this.openPreview,
              },
              {
                label: "Download Preview",
                onClick: this.downloadPreview,
              },
              {
                label: "Download with Absolute URLs",
                onClick: () => this.download(true),
              },
            ]}
          />

          <Button
            color={unsavedChanges ? "primary" : "greyed"}
            onClick={this.save}
            disabled={!unsavedChanges}
          >
            Save
          </Button>
        </ToolbarGroup>
      </Toolbar>
    );
  }
}

EditorToolbar.propTypes = {
  setEditorDisplayWidth: PropTypes.func.isRequired,
  toggleEditorBreakdown: PropTypes.func.isRequired,
  updateEmail: PropTypes.func.isRequired,
  email: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }).isRequired,
  unsavedChanges: PropTypes.bool.isRequired,
  breakdown: PropTypes.bool.isRequired,
  displayWidth: PropTypes.number.isRequired,
};

// Connect state data
function mapStateToProps(state) {
  const { email, unsavedChanges, breakdown, displayWidth } = state.editor;
  return {
    email,
    displayWidth,
    unsavedChanges,
    breakdown,
  };
}

// Connect actions
export default connect(mapStateToProps, {
  setEditorDisplayWidth,
  toggleEditorBreakdown,
  updateEmail,
  openModal,
  fetchEmailQA,
  updateEditorNameValue,
  downloadEmail,
})(EditorToolbar);
